// API Service Layer for Remori Backend Integration

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://192.168.68.54:3003/api';
const API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT, 10) || 10000; // 10 seconds default

// Token management
const TOKEN_KEY = 'remori_token';

export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

export const setToken = (token) => {
  localStorage.setItem(TOKEN_KEY, token);
};

export const removeToken = () => {
  localStorage.removeItem(TOKEN_KEY);
};

// API request wrapper with error handling
const apiRequest = async (endpoint, options = {}) => {
  const token = getToken();
  
  const config = {
    method: options.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers,
    },
    ...options,
  };

  if (options.body && typeof options.body === 'object') {
    config.body = JSON.stringify(options.body);
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), options.timeout ?? API_TIMEOUT);
  config.signal = controller.signal;

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    clearTimeout(timeoutId);

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.message || 'An error occurred');
      error.status = response.status;
      error.errors = data.errors || [];
      error.data = data;
      throw error;
    }

    return data;
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      const timeoutError = new Error('Request timeout');
      timeoutError.status = 408;
      timeoutError.errors = [];
      throw timeoutError;
    }

    if (error.status) {
      throw error;
    }

    const networkError = new Error('Network error. Please check your connection.');
    networkError.status = 0;
    networkError.errors = [];
    throw networkError;
  }
};

// ============================================
// Authentication API
// ============================================

export const authAPI = {
  /**
   * Register a new user
   * @param {string} name - User's name
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @returns {Promise<{user: Object, token: string}>}
   */
  register: async (name, email, password) => {
    const response = await apiRequest('/auth/register', {
      method: 'POST',
      body: { name, email, password },
    });
    
    if (response.data.token) {
      setToken(response.data.token);
    }
    
    return response.data;
  },

  /**
   * Login an existing user
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @returns {Promise<{user: Object, token: string}>}
   */
  login: async (email, password) => {
    const response = await apiRequest('/auth/login', {
      method: 'POST',
      body: { email, password },
    });
    
    if (response.data.token) {
      setToken(response.data.token);
    }
    
    return response.data;
  },

  /**
   * Get current authenticated user
   * @returns {Promise<{user: Object}>}
   */
  getCurrentUser: async () => {
    const response = await apiRequest('/auth/me');
    return response.data;
  },

  /**
   * Logout user (client-side only)
   */
  logout: () => {
    removeToken();
  },
};

// ============================================
// User Profile API
// ============================================

export const userAPI = {
  /**
   * Update user profile (without image)
   * @param {Object} profileData - Profile data to update
   * @returns {Promise<{user: Object}>}
   */
  updateProfile: async (profileData) => {
    const response = await apiRequest('/users/profile', {
      method: 'PUT',
      body: profileData,
    });
    return response.data;
  },

  /**
   * Update user profile with image upload
   * @param {Object} profileData - Profile data to update
   * @param {File} imageFile - Image file to upload
   * @returns {Promise<{user: Object}>}
   */
  updateProfileWithImage: async (profileData, imageFile) => {
    const token = getToken();
    const formData = new FormData();

    // Add text fields
    Object.entries(profileData).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        formData.append(key, value);
      }
    });

    // Add image if provided
    if (imageFile) {
      formData.append('profileImage', imageFile);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

    try {
      const response = await fetch(`${API_BASE_URL}/users/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          // DO NOT set Content-Type for FormData - browser sets it automatically
        },
        body: formData,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const data = await response.json();

      if (!response.ok) {
        const error = new Error(data.message || 'Failed to update profile');
        error.status = response.status;
        error.errors = data.errors || [];
        error.data = data;
        throw error;
      }

      return data.data;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error.name === 'AbortError') {
        const timeoutError = new Error('Request timeout');
        timeoutError.status = 408;
        timeoutError.errors = [];
        throw timeoutError;
      }

      if (error.status) {
        throw error;
      }

      const networkError = new Error('Network error. Please check your connection.');
      networkError.status = 0;
      networkError.errors = [];
      throw networkError;
    }
  },

  /**
   * Change user password
   * @param {string} currentPassword
   * @param {string} newPassword
   * @param {string} confirmPassword
   * @returns {Promise<{message: string}>}
   */
  changePassword: async (currentPassword, newPassword, confirmPassword) => {
    const response = await apiRequest('/users/password', {
      method: 'PUT',
      body: { currentPassword, newPassword, confirmPassword },
    });
    return response;
  },

  /**
   * Delete user account
   * @param {string} password - Current password for confirmation
   * @returns {Promise<{message: string}>}
   */
  deleteAccount: async (password) => {
    const response = await apiRequest('/users/account', {
      method: 'DELETE',
      body: { password },
    });
    return response;
  },

  /**
   * Get user settings
   * @returns {Promise<Object>} User settings
   */
  getSettings: async () => {
    const response = await apiRequest('/users/settings');
    return response.data.settings;
  },

  /**
   * Update user settings
   * @param {Object} settings - Settings to update
   * @returns {Promise<Object>} Updated settings
   */
  updateSettings: async (settings) => {
    const response = await apiRequest('/users/settings', {
      method: 'PUT',
      body: settings,
    });
    return response.data.settings;
  },
};

// ============================================
// Entries API
// ============================================

export const entriesAPI = {
  /**
   * Create a new entry
   * @param {Object} entryData - Entry data
   * @param {string} entryData.text - Entry text content (HTML)
   * @param {string} entryData.mood - Entry mood
   * @param {string} entryData.timestamp - Entry timestamp (ISO 8601)
   * @returns {Promise<{entry: Object}>}
   */
  createEntry: async (entryData) => {
    const response = await apiRequest('/entries', {
      method: 'POST',
      body: entryData,
    });
    return response.data;
  },

  /**
   * Get all entries for the current user
   * @param {Object} options - Query options
   * @param {string} options.mood - Filter by mood
   * @param {string} options.startDate - Filter from date
   * @param {string} options.endDate - Filter to date
   * @param {string} options.search - Search query
   * @param {number} options.limit - Number of entries to return
   * @param {string} options.lastEvaluatedKey - Pagination key
   * @param {string} options.sort - Sort order
   * @returns {Promise<{entries: Array, pagination: Object}>}
   */
  getEntries: async (options = {}) => {
    const queryParams = new URLSearchParams();
    
    if (options.mood) queryParams.append('mood', options.mood);
    if (options.startDate) queryParams.append('startDate', options.startDate);
    if (options.endDate) queryParams.append('endDate', options.endDate);
    if (options.search) queryParams.append('search', options.search);
    if (options.limit) queryParams.append('limit', options.limit.toString());
    if (options.lastEvaluatedKey) queryParams.append('lastEvaluatedKey', options.lastEvaluatedKey);
    if (options.sort) queryParams.append('sort', options.sort);

    const queryString = queryParams.toString();
    const endpoint = `/entries${queryString ? `?${queryString}` : ''}`;
    
    const response = await apiRequest(endpoint);
    return response.data;
  },

  /**
   * Get a single entry by ID
   * @param {string} entryId - Entry ID
   * @returns {Promise<{entry: Object}>}
   */
  getEntry: async (entryId) => {
    const response = await apiRequest(`/entries/${entryId}`);
    return response.data;
  },

  /**
   * Update an existing entry
   * @param {string} entryId - Entry ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<{entry: Object}>}
   */
  updateEntry: async (entryId, updateData) => {
    const response = await apiRequest(`/entries/${entryId}`, {
      method: 'PUT',
      body: updateData,
    });
    return response.data;
  },

  /**
   * Delete an entry
   * @param {string} entryId - Entry ID
   * @returns {Promise<{message: string}>}
   */
  deleteEntry: async (entryId) => {
    const response = await apiRequest(`/entries/${entryId}`, {
      method: 'DELETE',
    });
    return response;
  },

  /**
   * Get entry statistics
   * @param {Object} options - Query options
   * @param {string} options.startDate - Calculate stats from date
   * @param {string} options.endDate - Calculate stats to date
   * @returns {Promise<{stats: Object}>}
   */
  getStats: async (options = {}) => {
    const queryParams = new URLSearchParams();
    
    if (options.startDate) queryParams.append('startDate', options.startDate);
    if (options.endDate) queryParams.append('endDate', options.endDate);

    const queryString = queryParams.toString();
    const endpoint = `/entries/stats/summary${queryString ? `?${queryString}` : ''}`;
    
    const response = await apiRequest(endpoint);
    return response.data;
  },

  /**
   * Export all entries
   * @param {string} format - Export format: 'json' or 'csv'
   * @returns {Promise<Object>} Export data with entries array or CSV string
   */
  exportEntries: async (format = 'json') => {
    const token = getToken();
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

    try {
      const response = await fetch(`${API_BASE_URL}/entries/export?format=${format}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const data = await response.json();
        const error = new Error(data.message || 'Failed to export entries');
        error.status = response.status;
        error.errors = data.errors || [];
        throw error;
      }

      // For CSV format, return as text; for JSON, parse as JSON
      if (format === 'csv') {
        const csvText = await response.text();
        return csvText;
      } else {
        const data = await response.json();
        return data.data;
      }
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        const timeoutError = new Error('Request timeout');
        timeoutError.status = 408;
        throw timeoutError;
      }

      if (error.status) {
        throw error;
      }

      const networkError = new Error('Network error. Please check your connection.');
      networkError.status = 0;
      throw networkError;
    }
  },

  requestEmailExport: async () => {
    const token = getToken();
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

    try {
      const response = await fetch(`${API_BASE_URL}/entries/export/email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const data = await response.json();
        const error = new Error(data.message || 'Failed to request export');
        error.status = response.status;
        error.errors = data.errors || [];
        throw error;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        const timeoutError = new Error('Request timeout');
        timeoutError.status = 408;
        throw timeoutError;
      }
      
      if (error.status) {
        throw error;
      }

      const networkError = new Error('Network error. Please check your connection.');
      networkError.status = 0;
      throw networkError;
    }
  },

  /**
   * Clear all entries (delete all)
   * @param {string} password - Current password for confirmation
   * @returns {Promise<{deletedCount: number}>}
   */
  clearAllData: async (password) => {
    const response = await apiRequest('/entries/all', {
      method: 'DELETE',
      body: {
        password,
        confirmation: 'DELETE'
      },
    });
    return response.data;
  },

  /**
   * Rephrase entry text with AI and detect emotion
   * @param {string} text - Entry text to rephrase (HTML or plain text)
   * @param {string} style - Optional: 'professional', 'casual', 'poetic', 'concise' (default: 'professional')
   * @param {boolean} preserveEmotion - Optional: Whether to preserve original emotional tone (default: true)
   * @returns {Promise<{rephrasedText: string, detectedEmotion: string, confidence: number, reasoning: string}>}
   */
  rephraseWithAI: async (text, style = 'professional', preserveEmotion = true) => {
    const response = await apiRequest('/entries/ai/rephrase', {
      method: 'POST',
      body: { text, style, preserveEmotion },
    });
    return response.data;
  },

  /**
   * Detect emotion from entry text
   * @param {string} text - Entry text to analyze (HTML or plain text)
   * @returns {Promise<{detectedEmotion: string, confidence: number, reasoning: string}>}
   */
  detectEmotion: async (text) => {
    const response = await apiRequest('/entries/ai/detect-emotion', {
      method: 'POST',
      body: { text },
    });
    return response.data;
  },

  /**
   * Summarize entry text with AI
   * @param {string} text - Entry text to summarize (HTML or plain text)
   * @returns {Promise<{summary: string}>}
   */
  summarizeWithAI: async (text) => {
    const response = await apiRequest('/entries/ai/summarize', {
      method: 'POST',
      body: { text },
    });
    return response.data;
  },

  /**
   * Analyse entry: detect mood and generate a reflective summary in one call.
   * When includeRephrase is true the response also contains rephrasedText.
   * @param {string} text - Entry text (HTML or plain text)
   * @param {{ includeRephrase?: boolean, style?: string }} options
   * @returns {Promise<{detectedEmotion: string, confidence: number, reasoning: string, summary: string, rephrasedText?: string}>}
   */
  analyseWithAI: async (text, { includeRephrase = false, style = 'professional' } = {}) => {
    const response = await apiRequest('/entries/ai/analyse', {
      method: 'POST',
      body: { text, includeRephrase, style },
      timeout: 30000, // AI inference needs more headroom than the default 10 s
    });
    return response.data;
  },
};

// ============================================
// Insights API
// ============================================

export const insightsAPI = {
  /**
   * Get comprehensive insights dashboard with all analytics
   * @param {Object} options - Query options
   * @param {string} options.period - Time period: "month", "quarter", "year", "all"
   * @param {string} options.timezone - User's timezone
   * @returns {Promise<Object>} Complete dashboard data
   */
  getDashboard: async (options = {}) => {
    const queryParams = new URLSearchParams();
    
    if (options.period) queryParams.append('period', options.period);
    if (options.timezone) queryParams.append('timezone', options.timezone);

    const queryString = queryParams.toString();
    const endpoint = `/insights/dashboard${queryString ? `?${queryString}` : ''}`;
    
    const response = await apiRequest(endpoint);
    return response.data;
  },

  /**
   * Get mood patterns and trends
   * @param {string} period - Time period: "week", "month", "quarter", "year", "all"
   * @param {boolean} includeAnalysis - Include AI analysis
   * @returns {Promise<Object>} Patterns data
   */
  getPatterns: async (period = 'month', includeAnalysis = true) => {
    const response = await apiRequest(
      `/insights/patterns?period=${period}&includeAnalysis=${includeAnalysis}`
    );
    return response.data;
  },

  /**
   * Get AI-generated journal summary
   * @param {string} period - Time period: "week", "month", "quarter", "year"
   * @param {string} format - Format: "brief", "detailed"
   * @returns {Promise<Object>} Summary data
   */
  getSummary: async (period = 'month', format = 'detailed') => {
    const response = await apiRequest(
      `/insights/summary?period=${period}&format=${format}`
    );
    return response.data;
  },

  /**
   * Get mood forecast and predictions
   * @param {number} days - Number of days to forecast (1-30)
   * @returns {Promise<Object>} Forecast data
   */
  getForecast: async (days = 7) => {
    const response = await apiRequest(`/insights/forecast?days=${days}`);
    return response.data;
  },

  /**
   * Get personalized writing prompts
   * @param {number} count - Number of prompts (1-10)
   * @param {string} type - Prompt type: "reflection", "gratitude", "goals", "mixed"
   * @returns {Promise<Object>} Prompts data
   */
  getPrompts: async (count = 3, type = 'mixed') => {
    const response = await apiRequest(
      `/insights/prompts?count=${count}&type=${type}`
    );
    return response.data;
  },

  /**
   * Analyze an entry or text with AI
   * @param {Object} options - Analysis options
   * @param {string} options.entryId - Existing entry ID to analyze
   * @param {string} options.text - New text to analyze
   * @param {boolean} options.includeRecommendations - Include recommendations
   * @returns {Promise<Object>} Analysis results
   */
  analyzeEntry: async (options = {}) => {
    const body = {
      includeRecommendations: options.includeRecommendations ?? true
    };
    
    if (options.entryId) body.entryId = options.entryId;
    if (options.text) body.text = options.text;

    const response = await apiRequest('/insights/analyze', {
      method: 'POST',
      body,
    });
    return response.data;
  },

  /**
   * Get visualization data for charts
   * @param {string} type - Chart type: "timeline", "heatmap", "distribution", "comparison"
   * @param {string} period - Time period: "week", "month", "quarter", "year"
   * @param {string} groupBy - Group by: "day", "week", "month"
   * @returns {Promise<Object>} Visualization data
   */
  getVisualization: async (type, period = 'month', groupBy = 'day') => {
    const params = new URLSearchParams({ type, period, groupBy });
    const response = await apiRequest(`/insights/visualization?${params.toString()}`);
    return response.data;
  },
};

// ============================================
// Utility Functions
// ============================================

/**
 * Check if user is authenticated (has valid token)
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  return !!getToken();
};

/**
 * Format entry for display (add date property for compatibility)
 * @param {Object} entry - Entry from API
 * @returns {Object} Formatted entry
 */
export const formatEntryForDisplay = (entry) => {
  const timestamp = new Date(entry.timestamp);
  return {
    ...entry,
    id: entry.entryId, // For backward compatibility with existing components
    date: timestamp.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    }),
  };
};

/**
 * Format multiple entries for display
 * @param {Array} entries - Entries from API
 * @returns {Array} Formatted entries
 */
export const formatEntriesForDisplay = (entries) => {
  return entries.map(formatEntryForDisplay);
};

/**
 * Get full URL for profile image
 * @param {string} imagePath - Profile image path from user object
 * @returns {string|null} Full image URL or null
 */
export const getProfileImageUrl = (imagePath) => {
  if (!imagePath) return null;
  
  // If it's already a full URL, return as-is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // If it's a blob or data URL (local preview), return as-is
  if (imagePath.startsWith('blob:') || imagePath.startsWith('data:')) {
    return imagePath;
  }
  
  // Otherwise, it's a server path - prepend the base URL
  const baseUrl = API_BASE_URL.replace('/api', '');
  return `${baseUrl}${imagePath}`;
};

const apiClient = {
  auth: authAPI,
  user: userAPI,
  entries: entriesAPI,
  insights: insightsAPI,
  isAuthenticated,
  getToken,
  setToken,
  removeToken,
  formatEntryForDisplay,
  formatEntriesForDisplay,
  getProfileImageUrl,
  API_BASE_URL,
};

export default apiClient;
